import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, FolderOpen, Plus, Layers, MoreVertical, Pencil, Trash2, Music2, Clock, Sparkles, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { MusicCard } from '@/components/cards';
import { SongActionsMenu } from '@/components/dialogs';
import { LoadingState, EmptyState, UnauthorizedState } from '@/components/states';
import { useAuth } from '@/context/AuthContext';
import { fmtTime } from '@/context/PlayerContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const FALLBACK = 'https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=600';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
}

export default function Studio() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get('/projects'), api.get('/library')]);
      setProjects(p.data);
      setSongs(s.data);
    } catch { /* keep current state on transient error */ }
  }, []);

  useEffect(() => {
    if (!session) return;
    load().finally(() => setLoading(false));
  }, [session, load]);

  if (!session) return <UnauthorizedState />;
  if (loading) return <LoadingState />;

  const projectBySong = {};
  projects.forEach((p) => { if (p.main_song_id) projectBySong[p.main_song_id] = p.id; });

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            <SlidersHorizontal className="h-7 w-7 flex-shrink-0 text-brand" /> Studio
          </h1>
          <p className="mt-1 text-muted-foreground">Ton espace de travail musical.</p>
        </div>
        <button onClick={() => setNewOpen(true)} className="flex w-full flex-shrink-0 items-center justify-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-black btn-glow sm:w-auto" data-testid="studio-new-project-button">
          <Plus className="h-4 w-4" /> Nouveau projet
        </button>
      </div>

      <section>
        <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-semibold text-white">
          <FolderOpen className="h-5 w-5 text-brand" /> Projets en cours
        </h2>
        {projects.length === 0 ? (
          <EmptyState icon={FolderOpen} title="Aucun projet"
                      description="Crée un projet à partir d'un nouveau morceau ou d'un morceau existant de ta Bibliothèque."
                      action={<button onClick={() => setNewOpen(true)} className="rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-black btn-glow" data-testid="studio-empty-new-button">Nouveau projet</button>} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} onOpen={() => navigate(`/studio/${p.id}`)}
                           onChanged={load} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-semibold text-white">
          <Layers className="h-5 w-5 text-brand" /> Morceaux générés
        </h2>
        <p className="-mt-2 mb-4 text-sm text-muted-foreground">Raccourci vers tes morceaux. Ouvre-les dans le Studio pour gérer leurs versions.</p>
        {songs.length === 0 ? (
          <EmptyState icon={Layers} title="Aucun morceau" description="Génère ta première musique depuis Créer."
                      action={<button onClick={() => navigate('/create')} className="rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-black btn-glow">Créer</button>} />
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {songs.map((s) => (
              <MusicCard key={s.id} song={s} queue={songs.map((x) => ({
                song_id: x.id, title: x.title, slug: x.slug, cover_url: x.cover_url,
                audio_url: x.audio_url, style_text: x.style_text, duration_seconds: x.duration_seconds }))}
                actions={<SongActionsMenu song={s} existingProjectId={projectBySong[s.id]}
                                          onProjectCreated={load}
                                          onDeleted={(id) => setSongs((prev) => prev.filter((x) => x.id !== id))} />} />
            ))}
          </div>
        )}
      </section>

      <NewProjectDialog open={newOpen} onOpenChange={setNewOpen} songs={songs} onCreated={load} />
    </div>
  );
}

function ProjectCard({ project, onOpen, onChanged }) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [title, setTitle] = useState(project.title);
  const [displayTitle, setDisplayTitle] = useState(project.title);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { setDisplayTitle(project.title); }, [project.title]);

  const rename = async () => {
    try {
      const { data } = await api.patch(`/projects/${project.id}`, { title });
      setDisplayTitle(data.title);
      toast.success('Projet renommé');
      setRenameOpen(false);
      onChanged?.(); // refresh counts/list in the background (non-blocking)
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success('Projet supprimé (tes morceaux restent dans la Bibliothèque)');
      await onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="card-surface card-hover overflow-hidden" data-testid={`project-${project.id}`}>
      <button onClick={onOpen} className="block w-full text-left" data-testid={`project-open-${project.id}`}>
        <div className="relative aspect-video overflow-hidden">
          <img src={project.cover_url || FALLBACK} alt={project.title} loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-brand/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand">
            <FolderOpen className="h-3 w-3" /> Projet
          </span>
          <div className="pointer-events-none absolute bottom-3 left-3 right-3">
            <h3 className="truncate font-display text-lg font-bold text-white">{displayTitle}</h3>
            <p className="truncate text-xs text-muted-foreground">{project.main_song_title || 'Sans version'}</p>
          </div>
        </div>
      </button>
      <div className="flex items-center justify-between p-3">
        <div className="min-w-0">
          <p className="truncate font-mono text-xs text-brand">{project.style_text || 'Style libre'}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{project.version_count} version(s)</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(project.updated_at)}</span>
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onOpen} className="rounded-full bg-surface-card px-3 py-1.5 text-xs font-semibold text-white hover:bg-surface-cardHover" data-testid={`project-open-btn-${project.id}`}>Ouvrir</button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-surface-cardHover hover:text-white" data-testid={`project-menu-${project.id}`}>
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass w-44">
              <DropdownMenuItem onClick={() => { setTitle(project.title); setRenameOpen(true); }} data-testid={`project-rename-${project.id}`}>
                <Pencil className="mr-2 h-4 w-4" /> Renommer
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setConfirmDelete(true)} className="text-destructive focus:text-destructive" data-testid={`project-delete-${project.id}`}>
                <Trash2 className="mr-2 h-4 w-4" /> Supprimer le projet
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="glass" data-testid="rename-project-dialog">
          <DialogHeader>
            <DialogTitle>Renommer le projet</DialogTitle>
            <DialogDescription>Choisis un nouveau nom pour ce projet Studio.</DialogDescription>
          </DialogHeader>
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="rename-project-input"
                 className="w-full rounded-xl border border-border bg-surface-input px-4 py-3 text-white focus:border-brand/60 focus:outline-none" />
          <DialogFooter>
            <button onClick={rename} disabled={!title.trim()} className="w-full rounded-full bg-brand py-2.5 font-semibold text-black btn-glow disabled:opacity-40" data-testid="rename-project-confirm">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="glass">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce projet ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le projet sera supprimé. Tes morceaux (versions) restent dans ta Bibliothèque et ne sont pas supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-project-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-project-delete">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NewProjectDialog({ open, onOpenChange, songs, onCreated }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('choice'); // choice | from-song
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setMode('choice'); setSelected(null); } }, [open]);

  const completed = songs.filter((s) => s.generation_status === 'completed');

  const createFromSong = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post('/projects/from-song', { song_id: selected });
      onOpenChange(false);
      if (data.already_existed) toast.info('Un projet existait déjà pour ce morceau — ouverture.');
      else toast.success('Projet créé');
      onCreated?.();
      navigate(`/studio/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass" data-testid="new-project-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau projet</DialogTitle>
          <DialogDescription>
            {mode === 'choice' ? 'Comment veux-tu démarrer ce projet ?' : 'Choisis un morceau de ta Bibliothèque.'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'choice' ? (
          <div className="space-y-3">
            <button onClick={() => { onOpenChange(false); navigate('/create'); }}
                    data-testid="new-project-create-song"
                    className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface-card p-4 text-left hover:border-brand/50">
              <Sparkles className="h-6 w-6 text-brand" />
              <div>
                <p className="font-semibold text-white">Créer un nouveau morceau</p>
                <p className="text-xs text-muted-foreground">Ouvre le moteur de création (Créer).</p>
              </div>
            </button>
            <button onClick={() => setMode('from-song')}
                    data-testid="new-project-from-song"
                    className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface-card p-4 text-left hover:border-brand/50">
              <FolderPlus className="h-6 w-6 text-brand" />
              <div>
                <p className="font-semibold text-white">Partir d'un morceau existant</p>
                <p className="text-xs text-muted-foreground">Crée un projet à partir d'un morceau de ta Bibliothèque.</p>
              </div>
            </button>
          </div>
        ) : (
          <>
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {completed.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Aucun morceau prêt dans ta Bibliothèque.</p>}
              {completed.map((s) => (
                <button key={s.id} onClick={() => setSelected(s.id)}
                        data-testid={`new-project-song-${s.id}`}
                        className={`flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition-all ${
                          selected === s.id ? 'border-brand bg-brand/10' : 'border-border bg-surface-card hover:border-brand/40'}`}>
                  <img src={s.cover_url || FALLBACK} alt="" className="h-11 w-11 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-white">{s.title}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      <Music2 className="mr-1 inline h-3 w-3" />{s.style_text || 'Style libre'}
                      {s.duration_seconds ? ` · ${fmtTime(s.duration_seconds)}` : ''}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <button onClick={() => setMode('choice')} className="rounded-full bg-surface-card px-4 py-2.5 text-sm font-semibold text-white hover:bg-surface-cardHover">Retour</button>
              <button onClick={createFromSong} disabled={!selected || busy}
                      className="flex-1 rounded-full bg-brand py-2.5 font-semibold text-black btn-glow disabled:opacity-40"
                      data-testid="new-project-create-confirm">
                {busy ? 'Création...' : 'Créer le projet'}
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
